import Image from 'next/image'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogAction,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { useAPIPelaksanaanTargetPerilaku } from 'api/kinerja/useAPIKinerja'
import { getIndicatorIllustrationPath } from '../utils/indicator'

import type { ResponsePelaksanaanTargetPerilaku } from 'types/kinerja-pelaksanaan'

type RubrikObservasiDialogProps = Omit<DialogProps, 'children'> & {
  contentId: string
  indicator: string
  definition: string
  isLoadingIndicator?: boolean
  illustrationAssetPath?: string
}

export function RubrikObservasiDialog({
  contentId,
  indicator,
  definition,
  isLoadingIndicator = false,
  illustrationAssetPath = '',
  isOpen,
  onClose,
  ...dialogProps
}: RubrikObservasiDialogProps) {
  const { isLoading, data: targetPerilakuData } =
    useAPIPelaksanaanTargetPerilaku(contentId, {
      enabled: !isLoadingIndicator && !!contentId && isOpen,
      staleTime: 30 * 60 * 1000, // stale after 30 min, force to refetch upon mounting
    })

  return (
    <Dialog size="lg" {...dialogProps} isOpen={isOpen} autoFocus={false}>
      <DialogOverlay />
      <DialogContent className="!p-0">
        <DialogHeader
          title="Rubrik Observasi Praktik Kinerja"
          subtitle="Berikut Target Perilaku yang akan diobservasi berdasarkan indikator yang dipilih pada tahap Perencanaan."
        />

        <div className="py-6 px-12">
          <div className="flex space-x-4 border border-solid rounded-lg max-w-2xl p-4 mb-6">
            <div className="flex-shrink-0">
              {isLoadingIndicator ? (
                <Shimmer width={84} height={84} />
              ) : (
                <Image
                  width={84}
                  height={84}
                  src={
                    illustrationAssetPath ||
                    getIndicatorIllustrationPath(indicator)
                  }
                  alt=""
                />
              )}
            </div>

            {isLoadingIndicator ? (
              <IndicatorShimmer />
            ) : (
              <div className="space-y-2">
                <Text variant="action">{indicator}</Text>
                <Text variant="body" color="subdued">
                  {definition}
                </Text>
              </div>
            )}
          </div>

          <Text variant="heading-sm" className="mb-4">
            Target Perilaku:
          </Text>

          {isLoading || isLoadingIndicator ? (
            <BehavioralTargetShimmer />
          ) : (
            targetPerilakuData?.data?.map((behavioralTarget, index) => (
              <BehavioralTargetItem
                key={index}
                orderNumber={index + 1}
                {...behavioralTarget}
              />
            ))
          )}
        </div>

        <DialogFooter>
          <DialogAction
            as={Button}
            type="button"
            color="blue"
            size="lg"
            onClick={() => onClose()}
          >
            Saya mengerti
          </DialogAction>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type BehavioralTargetItemProps = ResponsePelaksanaanTargetPerilaku & {
  orderNumber: number
}

function BehavioralTargetItem({
  id,
  orderNumber,
  behavioralTarget,
  recommendedBehaviors,
  prohibitedBehaviors,
}: BehavioralTargetItemProps) {
  return (
    <div key={id}>
      <div className="flex space-x-3 mb-4">
        <div className="bg-blue-70 w-8 h-8 text-white rounded-full text-center align-middle leading-loose flex-shrink-0">
          {orderNumber}
        </div>
        <Text variant="heading-sm" className="self-center !text-black">
          {behavioralTarget}
        </Text>
      </div>
      <div className="flex pl-12 space-x-6">
        <div className="flex flex-col w-full space-y-3">
          <div className="flex space-x-1 items-center">
            <Icon color="success">check_circle_outlined</Icon>
            <Text color="subdued" variant="subheading" className="uppercase">
              Perilaku yang Dianjurkan
            </Text>
          </div>
          <ul className="list-disc ml-7">
            {recommendedBehaviors.map((recommendedBehavior: string) => (
              <Text
                as="li"
                variant="body"
                color="subdued"
                key={recommendedBehavior}
              >
                {recommendedBehavior}
              </Text>
            ))}
          </ul>
        </div>
        <div className="flex flex-col w-full space-y-3">
          <div className="flex space-x-1 ml-0.5 items-center">
            <Icon color="critical">cancel_outlined</Icon>
            <Text color="subdued" variant="subheading" className="uppercase">
              Perilaku yang Dihindari
            </Text>
          </div>
          <ul className="list-disc ml-7">
            {prohibitedBehaviors.map((prohibitedBehavior) => (
              <Text
                as="li"
                variant="body"
                color="subdued"
                key={prohibitedBehavior}
              >
                {prohibitedBehavior}
              </Text>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function BehavioralTargetShimmer() {
  return (
    <div className="space-y-6">
      {new Array(3).fill('').map((_x, i) => (
        <div className="space-y-4" key={i}>
          <Shimmer className="!w-8/12 !h-6" />

          <div className="flex space-x-6">
            <Shimmer className="!w-full !h-32" />
            <Shimmer className="!w-full !h-32" />
          </div>
        </div>
      ))}
    </div>
  )
}

function IndicatorShimmer() {
  return (
    <div className="space-y-2 w-full">
      <Shimmer className="!w-64" />
      <Shimmer className="!w-full" noOfLines={3} />
    </div>
  )
}
